<!-- AI Dashboard Tab -->

<!-- ── 1. Loading: initial API call or S3 HTML download ──────────────────── -->
@if (initialLoading || srcLoading) {
<div class="flex flex-col h-full w-full items-center justify-center gap-3 p-6">
    <!-- Loading spinner Lottie while fetching initial status or rendering dashboard -->
    <app-lottie-animation [animationData]="queueAnimationData" [loop]="true" [autoplay]="true" width="80px"
        height="80px">
    </app-lottie-animation>
    <p class="text-sm font-medium text-gray-500 font-bricolage">
        {{ srcLoading ? 'Rendering dashboard…' : 'Loading AI Dashboard…' }}
    </p>
</div>

<!-- ── 2. Generation in progress ─────────────────────────────────────────── -->
} @else if (aiDashboardStatus() === GEOTECHNICAL_AI_DASHBOARD_STATUS.IN_PROGRESS && !creditsExhausted) {
<div class="flex flex-col h-full w-full justify-between">

    <div class="flex flex-col items-center gap-1 pt-8 px-6">
        <!-- Gradient title matching the "Preparing your AI Dashboard" screenshot -->
        <p class="font-bold text-xl md:text-2xl font-bricolage theme-text text-transparent text-center">
            Preparing your AI Dashboard
        </p>
        <p class="text-sm theme-text font-bricolage text-center">
            Please wait while we analyze your documents and generate actionable insights.
        </p>
    </div>

    <!-- Generation Lottie animation (bubble/sphere effect) -->
    <div class="relative flex items-center justify-center w-full h-56">
        <app-lottie-animation [animationData]="generationAnimationData" [loop]="true" [autoplay]="true" width="200px"
            height="200px" containerClass="geo-technical-summary-lottie">
        </app-lottie-animation>
    </div>
    <div class="flex items-end justify-center overflow-hidden">
        <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.AI_DASHBOARD_FOOTER | sanitizeHtml"
            class="[&>svg]:w-[500px] [&>svg]:h-[220px] pl-5" alt="AI Dashboard"></i>
    </div>

</div>

<!-- ── 3. Generation failed ───────────────────────────────────────────────── -->
} @else if (aiDashboardStatus() === GEOTECHNICAL_AI_DASHBOARD_STATUS.FAILED) {
<div class="flex flex-col h-full w-full justify-between">

    <div class="flex flex-col items-center gap-1 pt-8 px-6">
        <!-- Dark title + neutral subtitle matching the "Failed" screenshot -->
        <p class="font-bold text-xl md:text-2xl font-bricolage text-gray-800 text-center">
            Failed to load AI Dashboard
        </p>
        <p class="text-sm text-gray-500 font-bricolage text-center">
            We couldn't prepare your AI dashboard right now. Please retry.
        </p>
    </div>

    <!-- Orange Retry button with rotate-ccw icon, matching screenshot -->
    <div class="flex items-center justify-center">
        <app-button label="Retry" icon="rotate-ccw" iconSource="lucide" severity="primary" variant="filled"
            (clicked)="onGenerate()">
        </app-button>
    </div>

    <div class="flex items-end justify-center overflow-hidden">
        <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.AI_DASHBOARD_FOOTER | sanitizeHtml"
            class="[&>svg]:w-[500px] [&>svg]:h-[220px] pl-5" alt="AI Dashboard"></i>
    </div>

</div>

<!-- ── 3b. Credits exhausted ──────────────────────────────────────────────── -->
} @else if (creditsExhausted) {
<div class="flex flex-col h-full w-full">
    <div class="flex flex-1 items-center justify-center">
        <app-credits-overview variant="exhaustionlayout" feature="GEOTECHNICAL_AI_DASHBOARD"
            [featureId]="analysisId" [forceExhausted]="creditsExhausted"
            (resume)="_loadPersistedDashboard()" />
    </div>
    <div class="flex items-end justify-center overflow-hidden">
        <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.AI_DASHBOARD_FOOTER | sanitizeHtml"
            class="[&>svg]:w-[500px] [&>svg]:h-[220px] pl-5" alt="AI Dashboard"></i>
    </div>
</div>

<!-- ── 4. NOT_STARTED — queued ────────────────────────────────────────────── -->
} @else if (aiDashboardStatus() === GEOTECHNICAL_AI_DASHBOARD_STATUS.NOT_STARTED) {
<div class="flex flex-col h-full w-full justify-between">

    <div class="flex flex-col items-center gap-1 pt-8 px-6">
        <!-- Gradient title + orange subtitle matching the "In Queue" screenshot -->
        <p class="font-bold text-xl md:text-2xl font-bricolage theme-text text-transparent text-center">
            Your AI Dashboard is in queue
        </p>
        <p class="text-sm theme-text font-bricolage text-center">
            Your AI dashboard request is waiting its turn and will begin soon.
        </p>
    </div>

    <!-- Loading spinner Lottie for the queued state -->
    <div class="flex items-center justify-center">
        <app-lottie-animation [animationData]="queueAnimationData" [loop]="true" [autoplay]="true" width="80px"
            height="80px">
        </app-lottie-animation>
    </div>

    <div class="flex items-end justify-center overflow-hidden">
        <i [innerHTML]="GEOTECHNICAL_ANALYSIS_SVG.AI_DASHBOARD_FOOTER | sanitizeHtml"
            class="[&>svg]:w-[500px] [&>svg]:h-[220px] pl-5" alt="AI Dashboard"></i>
    </div>

</div>

<!-- ── 5. Dashboard ready ─────────────────────────────────────────────────── -->
} @else if (aiDashboardSrc) {
<div class="flex flex-col h-full w-full min-h-0">

    @if (openHistory) {

    <!-- ── 5a. History panel ─────────────────────────────────────────────── -->
    <div class="flex flex-col h-full p-3 w-full">
        <div class="flex items-center gap-2">
            <app-button [icon]="SVG.GO_BACK" [iconSource]="'svg'" [size]="'default'" variant="text"
                (clicked)="openHistory = false">
            </app-button>
            <p class="font-semibold text-lg">AI Dashboard</p>
        </div>

        @if (loadingHistory) {
        <div class="flex justify-center items-center flex-1 p-6">
            <div class="loader"></div>
        </div>
        } @else if (historyList.length === 0) {
        <div class="flex flex-col items-center justify-center flex-1 p-6 text-gray-500">
            <span>No version history yet.</span>
        </div>
        } @else {
        <div class="flex flex-col p-4 overflow-auto">
            @for (item of historyList; track item._id) {
            <div class="flex flex-row items-start justify-between gap-3 py-3 border-b last:border-b-0 rounded-lg px-3 transition-colors"
                [class.border-orange-200]="item._id === currentVersionId"
                [class.border-gray-200]="item._id !== currentVersionId"
                [class.bg-orange-50]="item._id === currentVersionId">
                <div class="flex flex-col gap-1 min-w-0 flex-1">
                    <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-sm font-medium text-gray-800">{{ item.name }}</span>
                    </div>
                    <span class="text-xs text-gray-500">{{ item.createdAt | date:'d MMMM yyyy, h:mm a' }}</span>
                    <app-user-name-display [user]="item.createdBy" [variant]="'avatar-name'">
                    </app-user-name-display>
                </div>
                <div class="flex gap-2 items-center justify-between ">
                    @if (item._id === currentVersionId) {
                    <span
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-orange-500 text-white leading-none">
                        Current Version
                    </span>
                    }

                    <app-menu #versionItemMenu [items]="getVersionMenuItems(item)" class="flex-shrink-0">
                        <div class="p-1.5 rounded-base hover:bg-gray-100 transition-colors cursor-pointer text-gray-500 flex items-center justify-center"
                            (click)="$event.stopPropagation(); versionItemMenu.toggle($event)">
                            <i class="pi pi-ellipsis-v text-base"></i>
                        </div>
                    </app-menu>
                </div>

            </div>
            }
        </div>
        }
    </div>

    } @else {

    <!-- ── 5b. Dashboard iframe view ─────────────────────────────────────── -->
    <div class="flex flex-shrink-0 justify-between items-center px-5 py-3 border-b border-gray-100 w-full">
        <div class="flex items-center font-semibold text-base gap-2 min-w-0">
            <span scrollReset contenteditable="true"
                class="truncate rounded px-1 -mx-1 hover:bg-gray-100 focus:bg-gray-100 cursor-text transition-colors"
                (keydown.enter)="blurTarget($event)" (blur)="onRenameCurrentVersion($event)">{{ currentVersionName ||
                'AI Dashboard' }}</span>
        </div>
        <div class="flex gap-2">
            <app-button [icon]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [iconSource]="'svg'" [size]="'default'"
                variant="outlined" severity="contrast" pTooltip="Re-Generate" tooltipPosition="top"
                (clicked)="onRegenerate()">
            </app-button>
            <app-button [icon]="SVG.DOWNLOAD" [iconSource]="'svg'" [size]="'default'" variant="outlined"
                severity="contrast" pTooltip="Download" tooltipPosition="top" [disabled]="downloadLoading()"
                (clicked)="onDownload()">
            </app-button>
            <app-button [icon]="SVG.VERSION" [iconSource]="'svg'" [size]="'default'" variant="outlined"
                pTooltip="History" tooltipPosition="top" (clicked)="onHistoryClick()">
            </app-button>
        </div>
    </div>

    <div class="flex-1 min-h-0 p-3">
        <iframe [src]="aiDashboardSrc" class="w-full h-full border-0 rounded-xl shadow-sm"
            sandbox="allow-scripts allow-same-origin" title="AI Dashboard">
        </iframe>
    </div>

    }

</div>

<!-- ── 6. Empty — no dashboard yet ───────────────────────────────────────── -->
} @else {
<div class="flex flex-col h-full w-full items-center justify-center gap-5 p-6">
    <div class="flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200">
        <app-icon [type]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [source]="'svg'" [size]="'lg'" />
    </div>
    <div class="flex flex-col items-center gap-1.5">
        <p class="font-semibold text-lg text-gray-800">No AI dashboard yet</p>
        <p class="text-sm text-gray-400 text-center max-w-xs">
            Generate an interactive AI-powered dashboard from your geotechnical analysis data.
        </p>
    </div>
    <app-button [icon]="GEOTECHNICAL_ANALYSIS_SVG.AI_GENERATED" [iconSource]="'svg'" label="Generate AI Dashboard"
        [size]="'default'" variant="filled" severity="primary" (clicked)="onGenerate()">
    </app-button>
</div>
}

<!-- ── Re-generate instructions dialog ───────────────────────────────────── -->
<app-dialog [visible]="regenDialogVisible" (closed)="regenDialogVisible = false" title="Re-Generate AI Dashboard"
    class="max-w-[40rem]">
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-3">
            <p class="text-xs text-gray-400">
                Optionally provide instructions to customise the dashboard — focus areas, KPIs to highlight, preferred
                layout, etc. Leave blank to regenerate with default settings.
            </p>

            <!-- Workspace-level instructions badge — shown when textarea is pre-filled from workspace config -->
            @if (instructionsFromWorkspace) {
            <div
                class="flex items-center gap-1.5 self-start bg-blue-50 border border-blue-200 text-blue-600 text-xs font-medium px-2.5 py-1 rounded-full">
                <i class="pi pi-building text-[10px]"></i>
                <span>Workspace default instructions</span>
            </div>
            }

            <textarea placeholder="e.g. Highlight cost savings, focus on top 3 layers, show schedule impact…" rows="5"
                class="w-full p-3 border border-gray-300 rounded-base text-sm outline-none placeholder:italic placeholder:text-gray-400 focus:border-orange-500 resize-none"
                [(ngModel)]="regenInstructions" (input)="instructionsFromWorkspace = false"></textarea>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end">
            <app-button label="Submit" severity="primary" variant="filled" (clicked)="onSubmitRegenDialog()" />
        </div>
    </ng-template>
</app-dialog>

<!-- ── View version dialog ───────────────────────────────────────────────── -->
<app-dialog [visible]="viewDialogVisible" (closed)="onCloseViewDialog()" [title]="viewVersionTitle"
    class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        @if (viewVersionLoading) {
        <div class="flex justify-center items-center h-64">
            <p-progressSpinner strokeWidth="4" [style]="{width: '2.5rem', height: '2.5rem'}"></p-progressSpinner>
        </div>
        } @else if (viewVersionSrc) {
        <iframe [src]="viewVersionSrc" class="w-full border-0 rounded-xl" style="height: 70vh;"
            sandbox="allow-scripts allow-same-origin" title="Version Preview">
        </iframe>
        }
    </ng-template>
</app-dialog>

<!-- ── Rename version dialog ─────────────────────────────────────────────── -->
<app-dialog [visible]="renameDialogVisible" (closed)="renameDialogVisible = false" title="Rename Version"
    class="max-w-[30rem]">
    <ng-template #bodyTemplate>
        <input type="text" placeholder="Version name"
            class="w-full p-3 border border-gray-300 rounded-base text-sm outline-none placeholder:text-gray-400 focus:border-orange-500"
            [(ngModel)]="renameInput" (keydown.enter)="onSubmitRename()" />
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end">
            <app-button label="Save" severity="primary" variant="filled" (clicked)="onSubmitRename()" />
        </div>
    </ng-template>
</app-dialog>